/**
 * Custom chord type registrations for tonal.js
 *
 * Registers chord types that tonal.js doesn't recognize by default
 * but are commonly used in practice. These are registered at module
 * load time so they're available throughout the app.
 *
 * Note: This module has side effects - importing it registers the types.
 */
import { ChordType } from "tonal";

/**
 * Half-diminished with ø symbol
 * Tonal knows this as "m7b5" but doesn't recognize the ø notation
 */
ChordType.add(["1P", "3m", "5d", "7m"], ["ø7", "ø"], "half-diminished");

/**
 * Minor-major seventh aliases
 * Tonal knows "mmaj7" but not these common alternative notations
 */
ChordType.add(
  ["1P", "3m", "5P", "7M"],
  ["mMaj7", "m/maj7", "m(maj7)", "minMaj7"],
  "minor major seventh",
);

/**
 * Power chord + major 7th
 * Common in rock/metal - power chord with added major 7th
 */
ChordType.add(["1P", "5P", "7M"], ["5add7", "5maj7"], "fifth add seven");

/**
 * Major 7th #11 (Lydian chord)
 * Common jazz chord - major 7th with raised 11th
 */
ChordType.add(
  ["1P", "3M", "5P", "7M", "11A"],
  ["maj7#11", "M7#11", "Δ#11"],
  "major seventh sharp eleven",
);

/**
 * Major 11th
 * Major chord with major 7th, 9th, and 11th
 */
ChordType.add(
  ["1P", "3M", "5P", "7M", "9M", "11P"],
  ["maj11", "M11", "Δ11"],
  "major eleventh",
);

/**
 * Minor-major 11th
 * Minor chord with major 7th, 9th, and 11th
 */
ChordType.add(
  ["1P", "3m", "5P", "7M", "9M", "11P"],
  ["mmaj11", "mM11", "m/maj11"],
  "minor major eleventh",
);

/**
 * Minor-major 7th flat 5
 * Half-diminished quality with major 7th instead of minor 7th
 */
ChordType.add(
  ["1P", "3m", "5d", "7M"],
  ["mmaj7b5", "mM7b5", "m/maj7b5"],
  "minor major seventh flat five",
);

/**
 * Altered dominant (short form)
 * Tonal knows "alt7" but not just "alt"
 */
ChordType.add(["1P", "3M", "5A", "7m", "9A"], ["alt"], "altered");
